import type { ReactNode } from 'react'
import tw, { styled } from 'twin.macro'

import { focusStyles } from '../sharedStyles'
import type {
  SizeOptions,
  PaletteOptions,
  VariationOptions,
  StyledButtonProps,
} from './types'

// https://github.com/ben-rogerson/twin.macro/issues/17#issuecomment-629720647
function getButtonSize(
  size: SizeOptions,
  startSlot: ReactNode,
  endSlot: ReactNode,
) {
  const styleMap = {
    small: [
      tw`
      px-4 py-1
      text-xs-brand leading-6`,
      startSlot && tw`pl-2.5`,
      endSlot && tw`pr-2.5`,
    ],
    medium: [
      tw`
      px-5 py-2.5
      text-sm-brand`,
      startSlot && tw`pl-3.5`,
      endSlot && tw`pr-3.5`,
    ],
    large: [
      tw`
      px-6 py-3.5
      text-sm-brand`,
      startSlot && tw`pl-4`,
      endSlot && tw`pr-4`,
    ],
  }

  return styleMap[size]
}

export function getCombinedStyles(
  palette: PaletteOptions,
  variation: VariationOptions,
) {
  const styleMap = {
    action: {
      primary: tw`
        bg-brand-emphasis hover:bg-brand-emphasis disabled:bg-brand-emphasis
        text-inverted-subdued
        font-semibold`,
      secondary: tw`
        bg-brand-subdued hover:bg-brand-muted disabled:bg-brand-subdued
        text-brand-neutral
        font-semibold`,
      border: tw`
        bg-default-subdued hover:bg-brand-subdued disabled:bg-default-subdued
        text-brand-neutral
        border border-default-emphasis
        font-medium`,
      borderless: tw`
        bg-transparent hover:bg-brand-subdued disabled:bg-transparent
        text-brand-neutral
        font-medium`,
    },
    positive: {
      primary: tw`
        bg-success-emphasis disabled:bg-success-neutral
        text-inverted-subdued
        font-semibold`,
      secondary: tw`
        bg-success-subdued hover:bg-success-muted disabled:bg-success-subdued
        text-success-emphasis
        font-semibold`,
      border: tw`
        bg-default-subdued hover:bg-success-subdued disabled:bg-default-subdued
        text-success-neutral hover:text-success-emphasis
        border border-success-emphasis
        font-medium`,
      borderless: tw`
        bg-transparent hover:bg-success-subdued disabled:bg-transparent
        text-success-emphasis
        font-medium`,
    },
    negative: {
      primary: tw`
        bg-critical-emphasis disabled:bg-critical-neutral
        text-inverted-subdued
        font-semibold`,
      secondary: tw`
        bg-critical-subdued hover:bg-critical-muted disabled:bg-critical-subdued
        text-critical-emphasis
        font-semibold`,
      border: tw`
        bg-default-subdued hover:bg-critical-subdued disabled:bg-default-subdued
        text-critical-neutral
        border border-critical-emphasis
        font-medium`,
      borderless: tw`
        bg-transparent hover:bg-critical-subdued disabled:bg-transparent
        text-critical-neutral
        font-medium`,
    },
    neutral: {
      primary: tw`
        bg-inverted-emphasis hover:bg-inverted-emphasis disabled:bg-inverted-emphasis
        text-inverted-subdued
        font-semibold`,
      secondary: tw`
        bg-default-muted hover:bg-default-neutral disabled:bg-default-muted
        text-default-neutral
        font-semibold`,
      border: tw`
        bg-default-subdued hover:bg-default-muted disabled:bg-default-subdued
        text-default-neutral
        border border-default-emphasis
        font-medium`,
      borderless: tw`
        bg-default-subdued hover:bg-gray-50 disabled:bg-default-subdued
        text-default-neutral
        font-medium`,
    },
  }

  return styleMap[palette][variation]
}

export const StyledButton = styled.button<StyledButtonProps>(
  ({ size, palette, variation, fullWidth, centered, startSlot, endSlot }) => [
    focusStyles,
    tw`
      border
      border-transparent
      font-brand
      disabled:(opacity-50 cursor-default)
      items-center
      rounded
    `,
    ...getButtonSize(size, startSlot, endSlot),
    getCombinedStyles(palette, variation),
    fullWidth ? tw`flex w-full` : tw`inline-flex`,
    centered ? tw`justify-center` : tw`justify-between`,
  ],
)

function getSlotMargin(size: SizeOptions, position: 'start' | 'end') {
  const styleMap = {
    small: position === 'start' ? tw`mr-1` : tw`ml-1`,
    medium: position === 'start' ? tw`mr-2` : tw`ml-2`,
    large: position === 'start' ? tw`mr-2` : tw`ml-2`,
  }

  return styleMap[size]
}

export const StyledStarSlot = styled.div<{ size: SizeOptions }>(({ size }) => [
  getSlotMargin(size, 'start'),
])

export const StyledEndSlot = styled.div<{ size: SizeOptions }>(({ size }) => [
  getSlotMargin(size, 'end'),
])

export const LoadingLabel = tw.div`ml-3`
